import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import {
  BillingInfo,
  PaymentMethod,
  SimplePicker,
  StoredPaymentMethodSelect,
  TransactionCategory,
} from '@shipstation/financial-services-ui-components';

import { TextType } from 'ui-components/components/text';
import { emptyArray } from 'ui-components/default-props/empty';

import fsuiStyles from '../../../financial-services-ui/fsui-styles.module.less';
import { getPaymentMethodsFromArray } from '../../../financial-services-ui/mapping';
import { PickerTitle } from '../../../financial-services-ui/picker-title';
import { PaymentMethod as PaymentMethodStation } from '../../../enums/payment-method';
import { messages } from '../../../financial-services-ui/messages';

import type { CreditCardConfig, PayByBankConfig, StoredPaymentMethod } from '@shipstation/financial-services-ui-components';
import type { PaymentMethodDetails } from '../../../financial-services-ui/types/fsui';

const payByBankTypes = [PaymentMethod.PayByBankEU, PaymentMethod.PayByBankUS];

// PaymentMethodDetails (this app's payment method shape) and StoredPaymentMethod (the FSUI library's) describe the
// same backend payload but declare different, non-optional fields (enrolledProcessors, nickname, dateLastUsed).
// Convert explicitly at this boundary instead of casting between the two.
const toStoredPaymentMethod = (method: PaymentMethodDetails): StoredPaymentMethod => ({
  ...method,
  nickname: method.nickname ?? '',
  dateLastUsed: method.dateLastUsed ?? '',
  assignedPreferences: method.assignedPreferences ?? [],
  accountHolderName: method.accountHolderName ?? null,
  accountHolderAddress: method.accountHolderAddress ?? null,
  enrolledProcessors: [],
});

type PaymentMethodFormProps = {
  isAddingNew: boolean;
  onToggleAddingNew: () => void;
  paymentMethodDetails: PaymentMethodDetails[];
  subscriptionPaymentMethod: PaymentMethodDetails | undefined;
  onSelectSavedMethod: (method: StoredPaymentMethod | null) => void;
  isSubmitting: boolean;
  paymentType: PaymentMethod | null;
  onPaymentTypeChange: (type: PaymentMethod) => void;
  creditCardConfig: CreditCardConfig;
  payByBankUsConfig: PayByBankConfig;
  eligiblePaymentMethodTypes: PaymentMethod[];
  currencyCode: string;
  locale: string;
  onSetPerformFormValidation: (validationFn: (() => void) | null) => void;
};

export const PaymentMethodForm = ({
  isAddingNew,
  onToggleAddingNew,
  paymentMethodDetails,
  subscriptionPaymentMethod,
  onSelectSavedMethod,
  isSubmitting,
  paymentType,
  onPaymentTypeChange,
  creditCardConfig,
  payByBankUsConfig,
  eligiblePaymentMethodTypes,
  currencyCode,
  locale,
  onSetPerformFormValidation,
}: PaymentMethodFormProps) => {
  const intl = useIntl();
  const hasPaymentMethodsToPick = paymentMethodDetails.length > 0;
  const showBillingInfo =
    (isAddingNew && !(paymentType && payByBankTypes.includes(paymentType))) ||
    (!isAddingNew && subscriptionPaymentMethod?.paymentMethod !== PaymentMethodStation.ACH);

  const storedSubscriptionPaymentMethod = useMemo(
    () => (subscriptionPaymentMethod ? toStoredPaymentMethod(subscriptionPaymentMethod) : null),
    [subscriptionPaymentMethod],
  );
  const storedPaymentMethods = useMemo(
    () => getPaymentMethodsFromArray(paymentMethodDetails.map(toStoredPaymentMethod)),
    [paymentMethodDetails],
  );

  return (
    <>
      <PickerTitle
        hasPaymentMethodsToPick={hasPaymentMethodsToPick}
        isAdding={isAddingNew}
        labelTextType={TextType.Label}
        onClick={onToggleAddingNew}
      />
      {isAddingNew ? (
        <SimplePicker
          acceptedPaymentTypes={eligiblePaymentMethodTypes || emptyArray}
          creditCardConfig={creditCardConfig}
          currencyCode={currencyCode}
          locale={locale}
          onPaymentTypeChange={onPaymentTypeChange}
          payByBankConfig={payByBankUsConfig}
          showTitle={false}
        />
      ) : (
        <StoredPaymentMethodSelect
          className={hasPaymentMethodsToPick ? fsuiStyles.picker : fsuiStyles['picker-no-content']}
          disabled={!hasPaymentMethodsToPick || isSubmitting}
          emptyStateMessage={intl.formatMessage(messages.noPaymentMethodsAvailable)}
          hasError={false}
          initialValue={storedSubscriptionPaymentMethod}
          isFullWidth
          isLoading={isSubmitting}
          name='purchaseShipmentVolumePaymentMethodSelect'
          onChange={onSelectSavedMethod}
          paymentMethods={storedPaymentMethods}
          placeholder={intl.formatMessage(messages.selectPaymentMethodPlaceholder)}
          transactionCategory={TransactionCategory.Subscription}
        />
      )}
      {showBillingInfo && (
        <div className={fsuiStyles['billing-info-wrapper']}>
          <BillingInfo
            key={`${subscriptionPaymentMethod?.id ?? ''}-${isAddingNew}`}
            setPerformFormValidation={onSetPerformFormValidation}
            showEdit={false}
          />
        </div>
      )}
    </>
  );
};
